import {
  ClipBoxTool,
  ClipPlaneTool,
  Vector3,
  Web3DViewer,
} from '@technology/web3d';
import { TrimbimModel } from '@technology/web3d-plugin-trimbim';
import { extractEntitySourceIds } from '@trimble/tc-issue-management-web3d-utilities';
import {
  MeasurementsCapabilityProvider,
  MarkupsCapabilityProvider,
  PointCloudStylingCapabilityProvider,
  TrimBimCapabilityProvider,
  getTrbModelId,
  convertMeasurementToConnectMarkup,
  convertMarkupToConnectMarkup,
  convertViewerCameraToConnectCamera,
  convertViewerClipPlaneToConnectSectionPlane,
  convertViewerClipBoxToConnectSectionBox,
  convertPointCloudStylingToScan3dStyle,
  quaternionToRotation,
  type ViewerCameraData,
  type Measurement,
  type Markup as CustomMarkup,
} from '@trimble/web3d-providers';
import {
  Markup,
  Camera,
  Color,
  SectionPlane,
  SectionBox,
  Presentation,
  Element as PresentationElement,
} from '@data-access/connect/tc-tcps';
import type { ProjectDataState } from '../data-state/DataStateModels';
import type { TcTcpsApiService, TcRegion } from '@data-access/connect/tc-tcps';
import type { ScanDataApiService } from '@data-access/geospatial/scan-data';
import { LoadState } from '@trimble/data-loading';
import {
  getVisibleModelIds,
  getVisiblePointCloudIds,
} from '../data-state/DataContextProviderHelpers';
import type { CapabilityRegistry } from '@trimble/web3d';
import type { PaneManagerConfig } from '@trimble/pane-layout';
import { stitchScreenshots } from '@trimble/utilities';
import { getLogger } from '@trimble/logging';
import type { ITagItem } from '@tcweb/connect-ui';

const SEPARATOR_WIDTH = 2;
const SEPARATOR_COLOR = '#1b1b1d';

function getAllViewers(): Web3DViewer[] {
  return Array.from(document.querySelectorAll('web3d-viewer')) as Web3DViewer[];
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const response = await fetch(dataUrl);
  return response.blob();
}

async function captureMultipaneThumbnail<T>(
  layout: PaneManagerConfig<T>,
  rawBlobs: (Blob | null)[]
): Promise<string> {
  let blobIdx = 0;
  const rowBlobs = layout.paneRows
    .map((row) =>
      row.cells
        .map(() => rawBlobs[blobIdx++])
        .filter((b): b is Blob => b !== null)
    )
    .filter((row) => row.length > 0);

  if (rowBlobs.length === 0) {
    throw new Error('No valid screenshots to stitch');
  }

  const rowResults = await Promise.all(
    rowBlobs.map((row) =>
      stitchScreenshots(row, {
        forceEqualSizes: true,
        separatorWidth: SEPARATOR_WIDTH,
        separatorColor: SEPARATOR_COLOR,
      })
    )
  );

  if (rowResults.length === 1) {
    return rowResults[0].imageData;
  }

  const rowImageBlobs = await Promise.all(
    rowResults.map((r) => dataUrlToBlob(r.imageData))
  );

  const { imageData } = await stitchScreenshots(rowImageBlobs, {
    layout: 'vertical',
    forceEqualSizes: true,
    separatorWidth: SEPARATOR_WIDTH,
    separatorColor: SEPARATOR_COLOR,
  });

  return imageData;
}

export async function captureViewerState<T>(
  activeLayout?: PaneManagerConfig<T>
): Promise<{
  connectCamera: Camera;
  imageData: string;
  viewer: Web3DViewer;
}> {
  const viewers = getAllViewers();
  if (viewers.length === 0) {
    throw new Error('No 3D viewer found');
  }
  const viewer = viewers[0];

  const viewerCamera: ViewerCameraData = {
    position: viewer.camera.position,
    quaternion: viewer.camera.quaternion,
    rotation: quaternionToRotation(viewer.camera.quaternion),
    direction: viewer.camera.direction,
    projectionType: viewer.camera.projectionType,
    fieldOfView: viewer.camera.fieldOfView,
    orthoSize: viewer.camera.orthoSize,
  };

  const connectCamera = convertViewerCameraToConnectCamera(viewerCamera);

  const viewersToCapture = activeLayout ? viewers : [viewer];
  const rawBlobs = await Promise.all(
    viewersToCapture.map((v) => v.screenshot())
  );
  const blobs = rawBlobs.filter((b): b is Blob => b !== null);
  if (blobs.length === 0) {
    return { connectCamera, imageData: '', viewer };
  }

  const imageData =
    activeLayout && blobs.length > 1
      ? await captureMultipaneThumbnail(activeLayout, rawBlobs)
      : (await stitchScreenshots(blobs)).imageData;

  return { connectCamera, imageData, viewer };
}

/**
 * Collect visible model version IDs from data state
 */
export function collectVisibleModels(dataState: ProjectDataState): string[] {
  const visibleModelIds = getVisibleModelIds(dataState) ?? [];
  const modelVersionIds: string[] = [];

  const tcFileFolder = dataState.tcFileFolder;
  if (tcFileFolder.state === LoadState.LoadComplete) {
    visibleModelIds.forEach((modelId) => {
      const fileItem = tcFileFolder.items[modelId];
      if (fileItem && fileItem.type === 'FILE') {
        modelVersionIds.push(fileItem.versionId);
      }
    });
  }

  return modelVersionIds;
}

/**
 * Collect all markups (measurements + custom markups) from providers
 */
export function collectViewMarkups(
  capabilityRegistry: CapabilityRegistry
): Markup[] {
  const markups: Markup[] = [];

  const measurementsProvider = capabilityRegistry.get(
    MeasurementsCapabilityProvider
  )();
  if (measurementsProvider) {
    const measurements = measurementsProvider.items();
    measurements.forEach((measurement: Measurement) => {
      const markup = convertMeasurementToConnectMarkup(measurement);
      if (markup) {
        markups.push(markup);
      }
    });
  }

  // Get custom markups from MarkupsCapabilityProvider
  const markupsProvider = capabilityRegistry.get(MarkupsCapabilityProvider)();
  if (markupsProvider) {
    const customMarkups = markupsProvider.items();
    customMarkups.forEach((customMarkup: CustomMarkup) => {
      const markup = convertMarkupToConnectMarkup(customMarkup);
      if (markup) {
        markups.push(markup);
      }
    });
  }

  return markups;
}

/**
 * Collect current clipping (section planes) directly from a viewer.
 */
export function collectViewClipping(viewer: Web3DViewer): SectionPlane[] {
  const clipPlaneTool = viewer.tools.get(ClipPlaneTool);
  if (!clipPlaneTool) {
    return [];
  }
  return clipPlaneTool
    .getClipPlanes()
    .map((cp) =>
      convertViewerClipPlaneToConnectSectionPlane(cp.coplanarPoint, cp.normal)
    );
}

/**
 * Collect the current clip box (section box) directly from a viewer, if one exists.
 */
export function collectViewSectionBox(
  viewer: Web3DViewer
): SectionBox | undefined {
  const clipBoxTool = viewer.tools.get(ClipBoxTool);
  const clipBoxes = clipBoxTool?.getClipBoxes() ?? [];
  if (clipBoxes.length === 0) {
    return undefined;
  }
  const clipBox = clipBoxes[0];
  const size = new Vector3();
  clipBox.getBox().getSize(size);
  return convertViewerClipBoxToConnectSectionBox(
    clipBox.position,
    clipBox.quaternion,
    size
  );
}

const RGB_MAX_VALUE = 255;

// State 6 = "show" per Connect Element schema; the mobile-mapping loader ignores state but tcweb reads it.
const ELEMENT_STATE_SHOW = 6;
const ELEMENT_DEFAULT_PRIORITY = 0;

function findFirstViewerWithTrimbimModels(
  capabilityRegistry: CapabilityRegistry
): Web3DViewer | undefined {
  for (const container of capabilityRegistry.paneContainers()) {
    const viewer = container.get(TrimBimCapabilityProvider)()?.viewer;
    if (!viewer) continue;
    if (viewer.getModels(TrimbimModel).length > 0) return viewer;
  }
  return undefined;
}

// Materials are model-bound (shared across panes), so one viewer suffices.
async function collectPresentationElements(
  capabilityRegistry: CapabilityRegistry
): Promise<PresentationElement[]> {
  const viewer = findFirstViewerWithTrimbimModels(capabilityRegistry);
  if (!viewer) return [];

  const elements: PresentationElement[] = [];

  for (const model of viewer.getModels(TrimbimModel)) {
    const versionId = getTrbModelId(model.modelId);
    if (!versionId) continue;

    try {
      const entityIds = await model.getCustomMaterialEntityIds();
      if (entityIds.length === 0) continue;

      const [materials, sourceIds] = await Promise.all([
        model.getCustomMaterials(entityIds),
        extractEntitySourceIds(model, entityIds),
      ]);

      for (let i = 0; i < entityIds.length; i++) {
        const sourceId = sourceIds[i];
        const material = materials[i];
        if (!sourceId || !material) continue;
        if (material.color === undefined && material.opacity === undefined) {
          continue;
        }

        // Omit r/g/b when no color; loader reads r=g=b=0 as explicit black.
        const a = (material.opacity ?? 1) * RGB_MAX_VALUE;
        const color: Color = material.color
          ? {
              r: material.color.r * RGB_MAX_VALUE,
              g: material.color.g * RGB_MAX_VALUE,
              b: material.color.b * RGB_MAX_VALUE,
              a,
            }
          : ({ a } as Color);

        elements.push({
          sourceId,
          storageObjectId: '',
          state: ELEMENT_STATE_SHOW,
          isRecursive: false,
          priority: ELEMENT_DEFAULT_PRIORITY,
          versionId,
          color,
        });
      }
    } catch (error) {
      getLogger().warn(
        `Failed to collect presentation elements for model ${model.modelId}`,
        { error }
      );
    }
  }

  return elements;
}

// Reads from models, not the provider cache, so toolbar edits round-trip.
export async function collectPresentation(
  capabilityRegistry: CapabilityRegistry
): Promise<Presentation | undefined> {
  const elements = await collectPresentationElements(capabilityRegistry);
  if (elements.length === 0) return undefined;

  return { elements };
}

export interface CreateViewResult {
  readonly id: string;
  readonly markups: readonly Markup[];
  readonly measurements: readonly Measurement[];
}

/**
 * Parameters for creating a view with all associations
 */
export interface CreateViewParams {
  readonly name: string;
  readonly description: string;
  readonly tags: readonly ITagItem[];
  readonly sharedWith: readonly ITagItem[];
  readonly region: TcRegion;
  readonly project: { readonly id: string };
  readonly dataState: ProjectDataState;
  readonly capabilityRegistry: CapabilityRegistry;
  readonly tcTcpsApi: TcTcpsApiService;
  readonly scanDataApi: ScanDataApiService;
  readonly activeLayout?: PaneManagerConfig<unknown>;
}

export async function getViewData<T>(
  dataState: ProjectDataState,
  capabilityRegistry: CapabilityRegistry,
  activeLayout?: PaneManagerConfig<T>
): Promise<{
  connectCamera: Camera;
  imageData: string;
  modelVersionIds: string[];
  markups: Markup[];
  measurements: Measurement[];
  sectionPlanes: SectionPlane[];
  sectionBox: SectionBox | undefined;
  presentation: Presentation | undefined;
}> {
  const { connectCamera, imageData, viewer } =
    await captureViewerState(activeLayout);
  const modelVersionIds = collectVisibleModels(dataState);
  const markups = collectViewMarkups(capabilityRegistry);
  // Snapshot in markup order so labels aren't affected by edits made mid-save.
  const measurements = [
    ...(capabilityRegistry.get(MeasurementsCapabilityProvider)()?.items() ??
      []),
  ];
  const sectionPlanes = collectViewClipping(viewer);
  const sectionBox = collectViewSectionBox(viewer);
  const elementPresentation = await collectPresentation(capabilityRegistry);
  const globalOpacity = viewer.settings.globalOpacity;
  const PERCENTAGE_SCALE = 100;
  const presentation =
    elementPresentation || globalOpacity !== 1
      ? {
          ...elementPresentation,
          transparencyLevel: Math.round(globalOpacity * PERCENTAGE_SCALE),
          transparent: globalOpacity !== 1,
        }
      : undefined;
  return {
    connectCamera,
    imageData,
    modelVersionIds,
    markups,
    measurements,
    sectionPlanes,
    sectionBox,
    presentation,
  };
}

/**
 * createView should echo markups with server ids; refetch once when it does not,
 * but do not fail an already-created view if the refetch fails.
 */
export async function resolveCreatedMarkups(
  tcTcpsApi: Pick<TcTcpsApiService, 'getViewDetails'>,
  region: TcRegion,
  createdView: { readonly id: string; readonly markups?: Markup[] },
  submittedMarkupCount: number
): Promise<readonly Markup[]> {
  const created = createdView.markups ?? [];
  const needsRefetch =
    submittedMarkupCount > 0 &&
    (created.length !== submittedMarkupCount ||
      created.some((markup) => !markup.markupId));
  if (!needsRefetch) return created;

  try {
    // getViewDetails returns markups in creation order (same as the createView
    // response), so index correlation in collectConnectMeasurementData still holds.
    const detailed = await tcTcpsApi.getViewDetails(createdView.id, region);
    return detailed.markups ?? created;
  } catch (error) {
    getLogger().warn('Failed to refetch view markups for label correlation', {
      viewId: createdView.id,
      error,
    });
    return created;
  }
}

/**
 * Main orchestration function for creating a view with all associations
 */
export async function createViewWithAssociations(
  params: CreateViewParams
): Promise<CreateViewResult> {
  const {
    name,
    description,
    tags,
    sharedWith,
    region,
    project,
    dataState,
    capabilityRegistry,
    tcTcpsApi,
    scanDataApi,
    activeLayout,
  } = params;

  const {
    connectCamera,
    imageData,
    modelVersionIds,
    markups,
    measurements,
    sectionPlanes,
    sectionBox,
    presentation,
  } = await getViewData(dataState, capabilityRegistry, activeLayout);

  // Prepare assignees
  const assignees = sharedWith
    .filter((item) => item.id !== undefined && item.id !== '')
    .map((item) => ({
      id: item.id!,
      type: 'USER' as const,
    }));

  // Prepare view data
  const viewData = {
    name: name.trim(),
    description: description.trim(),
    projectId: project.id,
    models: modelVersionIds,
    camera: connectCamera,
    markups: markups.length > 0 ? markups : undefined,
    sectionPlanes: sectionPlanes.length > 0 ? sectionPlanes : undefined,
    sectionBox,
    imageData,
    assignees: assignees.length > 0 ? assignees : undefined,
  };

  // Create connect view
  const createdView = await tcTcpsApi.createView(region, viewData);

  if (!createdView?.id) {
    throw new Error('View creation failed: no view ID returned');
  }

  // Save associations (Point Cloud, Tags, Presentation)
  const visiblePointCloudIds = getVisiblePointCloudIds(dataState) ?? [];

  // Get current point cloud styling and convert to Scan3dStyle for saving
  const pointCloudStylingProvider = capabilityRegistry.get(
    PointCloudStylingCapabilityProvider
  )();
  const currentStyling = pointCloudStylingProvider?.pointCloudStyling();
  const scan3dStyle = currentStyling
    ? convertPointCloudStylingToScan3dStyle(currentStyling)
    : undefined;

  await scanDataApi.savePointCloudsForView(
    region,
    project.id,
    createdView.id,
    visiblePointCloudIds,
    scan3dStyle
  );

  await tcTcpsApi.saveTagsForView(
    region,
    project.id,
    createdView.id,
    tags.map((tag) => ({ id: tag.id, label: tag.label }))
  );

  if (presentation) {
    // From tcweb (TC3DV-1827): createView() does not allow entities with only
    // color opacity defined, but updateView()/updatePresentation() does.
    await tcTcpsApi.updatePresentationForView(
      region,
      createdView.id,
      presentation
    );
  }

  const createdMarkups = await resolveCreatedMarkups(
    tcTcpsApi,
    region,
    createdView,
    markups.length
  );

  return {
    id: createdView.id,
    markups: createdMarkups,
    measurements,
  };
}
